import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { protectedResources } from '../routes/AuthConfig';
import { useCallback } from 'react';

export const useAuth = () => {
  const { instance, inProgress, accounts } = useMsal();

  /**
   * Use MSAL to get the access token for the current user account. If the token
   * is expired we will attempt to refresh it using the loginRequest configuration.
   */
  const getAccessTokenOrRefresh = useCallback(
    async (baseUrl: string): Promise<string | null> => {
      if (inProgress === InteractionStatus.Startup) {
        console.debug('MSAL is still starting up, skipping token retrieval.');
        return null;
      }

      if (inProgress === InteractionStatus.HandleRedirect) {
        console.debug('MSAL is handling a redirect, skipping token retrieval.');
        return null;
      }

      let scopes: string[] = [];
      if (protectedResources.has(baseUrl)) {
        scopes = protectedResources.get(baseUrl) ?? [];
      }

      try {
        let account = instance.getActiveAccount();

        // If no active account but accounts exist, set the first one as active
        if (!account && accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
          account = accounts[0];
        }

        if (!account) {
          console.info('MSAL: No active account! Verify a user has been signed in.');
          return null;
        }

        const msalResponse = await instance.acquireTokenSilent({
          scopes,
          account: account,
        });

        if (msalResponse.accessToken === undefined) {
          throw Error('No id_token returned from auth request.');
        }

        return msalResponse.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.warn('MSAL: User interaction required to get new token, redirecting...');
          try {
            if (inProgress === InteractionStatus.None) {
              instance.logoutRedirect();
            }
          } catch (e) {
            console.error('Error getting new token for user:', e);
            return null;
          }
        }

        console.error('Error getting articles from CMS:', error);
        return null;
      }
    },
    [instance, inProgress, accounts]
  );

  return { getAccessTokenOrRefresh, msalStatus: inProgress };
};
